import React from 'react';
import { Link } from 'react-router-dom';
import Footer from "../components/layout/Footer";

const highlights = [
  {
    title: 'Covered sources',
    description: 'PubMed, FDA, Clinical Trials, and Google Patents only.',
  },
  {
    title: 'What is included',
    description: 'A plain overview of what is refreshed, how it is gathered, and how often it updates.',
  },
  {
    title: 'What is excluded',
    description: 'No endpoint lists, command details, or keyword logic.',
  },
];

const sourceCards = [
  {
    id: 'pubmed',
    title: 'PubMed',
    reason:
      'PubMed is refreshed to keep ophthalmic literature, related study signals, and publication activity current alongside the rest of the platform.',
    method:
      'The platform checks the relevant PubMed source paths, then brings new article records into the shared research view.',
    renewal: 'Renewed daily',
  },
  {
    id: 'fda',
    title: 'FDA',
    reason:
      'FDA data is refreshed to track ophthalmic device and drug activity, including approvals, clearances, and product updates.',
    method:
      'Records are gathered from the official FDA sources and kept in sync as new public entries appear.',
    renewal: 'Renewed daily',
  },
  {
    id: 'clinical-trials',
    title: 'Clinical Trials',
    reason:
      'Clinical trial data is refreshed to keep ophthalmic study status and trial details current.',
    method:
      'Study records are pulled from ClinicalTrials.gov and updated by trial ID so changes stay current without duplicating records.',
    renewal: 'Renewed daily',
  },
  {
    id: 'google-patents',
    title: 'Google Patents',
    reason:
      'Google Patents data is refreshed to track ophthalmic patent activity and related family updates.',
    method:
      'Patent records are collected from the public Google Patents dataset and refreshed alongside family information.',
    renewal: 'Renewed daily',
  },
];

const methodNotes = [
  {
    title: 'Official sources',
    description:
      'Each dataset comes from the original public source.',
  },
  {
    title: 'Plain cleanup',
    description:
      'Records are organized into a consistent format so they are easier to read and compare.',
  },
  {
    title: 'Regular renewal',
    description:
      'The platform refreshes these sources on a daily schedule.',
  },
];

const refreshSchedule = [
  { source: 'Clinical Trials', cadence: 'Daily' },
  { source: 'PubMed', cadence: 'Daily' },
  { source: 'FDA', cadence: 'Daily' },
  { source: 'Google Patents', cadence: 'Daily' },
];

const navLinkClass =
  'rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-300 transition hover:border-blue-400/50 hover:text-white';

const tocLinkClass =
  'rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-slate-300 transition hover:border-slate-500 hover:text-white';

const SectionHeading = ({ id, title, description }) => (
  <div id={id} className="max-w-3xl">
    <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-200">
      {title}
    </p>
    <p className="mt-3 text-base leading-7 text-slate-300">{description}</p>
  </div>
);

const SourceCard = ({ source }) => (
  <article className="rounded-3xl border border-white/10 bg-slate-950/70 p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.95)]">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-2xl font-semibold text-white">{source.title}</h2>
      <span className="rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">
        {source.renewal}
      </span>
    </div>

    <div className="mt-6 space-y-5 text-sm leading-7 text-slate-300">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Reason</p>
        <p className="mt-2">{source.reason}</p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Method</p>
        <p className="mt-2">{source.method}</p>
      </div>
    </div>
  </article>
);

const ApiDocs = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_32%),radial-gradient(circle_at_15%_15%,_rgba(14,165,233,0.14),_transparent_28%),linear-gradient(180deg,_rgba(2,6,23,1)_0%,_rgba(2,6,23,1)_100%)]" />

        <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <Link to="/login" className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg shadow-blue-950/40">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="1.5" fill="white" />
                </svg>
              </span>
              <span>
                <span className="block text-sm font-semibold text-white">Limbus Intelligence</span>
                <span className="block text-xs text-slate-400">Source documentation</span>
              </span>
            </Link>

            <div className="flex flex-wrap gap-3">
              <Link to="/api-docs" className={`${navLinkClass} border-blue-400/50 text-white`}>
                API Docs
              </Link>
              <Link to="/legal-docs" className={navLinkClass}>
                Legal Docs
              </Link>
              <Link to="/pricing" className={navLinkClass}>
                Pricing
              </Link>
            </div>
          </div>
        </header>

        <main className="relative">
          <section className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8">
            <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
              <div>
                <span className="inline-flex rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-blue-100">
                  Data Sources
                </span>
                <h1 className="mt-6 max-w-4xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                  How the data is refreshed across PubMed, FDA, Clinical Trials, and Google Patents
                </h1>
                <p className="mt-6 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">
                  This page gives a plain-language overview of what is refreshed, how it is
                  gathered, and how often each dataset updates. It intentionally avoids command
                  details, endpoint lists, and keyword logic.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a href="#sources" className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-200">
                    View sources
                  </a>
                  <a href="#schedule" className="rounded-full border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-200 transition hover:border-slate-400 hover:text-white">
                    View renewal schedule
                  </a>
                  <Link to="/legal-docs" className="rounded-full border border-blue-400/40 bg-blue-500/10 px-5 py-3 text-sm font-semibold text-blue-100 transition hover:border-blue-300 hover:bg-blue-500/15">
                    Review legal terms
                  </Link>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    { href: '#sources', label: 'Sources' },
                    { href: '#method', label: 'Method' },
                    { href: '#schedule', label: 'Renewal schedule' },
                    { href: '#notice', label: 'Usage notice' },
                  ].map((item) => (
                    <a key={item.href} href={item.href} className={tocLinkClass}>
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                {highlights.map((highlight) => (
                  <article
                    key={highlight.title}
                    className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_24px_60px_-40px_rgba(59,130,246,0.45)]"
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {highlight.title}
                    </p>
                    <p className="mt-3 text-base font-semibold leading-7 text-white">
                      {highlight.description}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="sources" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6 sm:p-8">
              <SectionHeading
                title="Sources"
                description="Only the four research sources below are documented here."
              />

              <div className="mt-8 grid gap-5 lg:grid-cols-2">
                {sourceCards.map((source) => (
                  <SourceCard key={source.id} source={source} />
                ))}
              </div>
            </div>
          </section>

          <section id="method" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6 sm:p-8">
              <SectionHeading
                title="Method"
                description="Across all four sources, the platform follows a consistent extraction pattern focused on traceability, normalization, and renewal."
              />

              <div className="mt-8 grid gap-5 md:grid-cols-3">
                {methodNotes.map((note) => (
                  <article
                    key={note.title}
                    className="rounded-3xl border border-white/10 bg-slate-950/70 p-6"
                  >
                    <h2 className="text-lg font-semibold text-white">{note.title}</h2>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{note.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="schedule" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6 sm:p-8">
                <SectionHeading
                  title="Renewal Schedule"
                  description="This is the current platform-level data renewal cadence for the four supported sources."
                />

                <div className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-slate-950/70">
                  <div className="grid grid-cols-2 border-b border-white/10 px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    <span>Source</span>
                    <span>Renewed</span>
                  </div>

                  {refreshSchedule.map((item) => (
                    <div
                      key={item.source}
                      className="grid grid-cols-2 border-b border-white/10 px-6 py-4 text-sm text-slate-300 last:border-b-0"
                    >
                      <span className="font-medium text-white">{item.source}</span>
                      <span>{item.cadence}</span>
                    </div>
                  ))}
                </div>
              </div>

              <aside
                id="notice"
                className="rounded-[2rem] border border-white/10 bg-slate-900/50 p-6"
              >
                <h2 className="text-lg font-semibold text-white">Usage notice</h2>
                <p className="mt-4 text-sm leading-7 text-slate-300">
                  These datasets are gathered from public sources and cleaned for search and
                  analysis.
                </p>
                <p className="mt-4 text-sm leading-7 text-slate-300">
                  Important decisions should always be checked against the original source record.
                </p>
                <Link
                  to="/legal-docs"
                  className="mt-6 inline-flex rounded-full border border-blue-400/40 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-blue-100 transition hover:border-blue-300 hover:bg-blue-500/15"
                >
                  Read the legal disclaimer
                </Link>
              </aside>
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default ApiDocs;
